import {
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Avatar,
  useTheme,
  alpha,
  Grid,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Warning } from '@mui/icons-material';
import type { WorkoutSegmentResponse } from '../../../../store/api/models/workoutModels';

interface SegmentCardProps {
  segment: WorkoutSegmentResponse;
  onClick?: () => void;
}

// Format duration from seconds to MM:SS or HH:MM:SS
const formatDuration = (seconds?: number | null): string => {
  if (!seconds) return '--:--';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format distance from meters to km
const formatDistance = (meters?: number | null): string => {
  if (!meters) return '--';
  return (meters / 1000).toFixed(2);
};

// Format pace from seconds per km to min:sec/km
const formatPace = (secondsPerKm?: number | null): string => {
  if (!secondsPerKm) return '--:--';
  const minutes = Math.floor(secondsPerKm / 60);
  const seconds = Math.floor(secondsPerKm % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Get compliance color hex based on percentage
const getComplianceColor = (percentage?: number | null): string => {
  if (!percentage || percentage < 60) return '#f44336'; // Red
  if (percentage >= 80) return '#4caf50'; // Green
  return '#ff9800'; // Orange (Warning)
};

// Format activity type name (warm_up -> Warm Up)
const formatActivityName = (activityType?: string | null): string => {
  if (!activityType) return 'Unknown';
  return activityType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Get activity type color
const getActivityColor = (activityType?: string | null): string => {
  const type = activityType?.toLowerCase();
  const colors: Record<string, string> = {
    warm_up: '#FF9800', // Orange
    run: '#2196F3', // Blue
    jog: '#4CAF50', // Green
    sprint: '#F44336', // Red
    walk: '#9C27B0', // Purple
    rest: '#607D8B', // Blue Grey
    ride: '#00BCD4', // Cyan
    float: '#3F51B5', // Indigo
  };
  return type ? (colors[type] || '#757575') : '#757575';
};

// Format target intensity value for display
const formatTargetIntensity = (segment: WorkoutSegmentResponse): string => {
  if (!segment.target_intensity_value || !segment.target_intensity_focus) {
    return 'No target';
  }

  const { target_intensity_focus, target_intensity_value } = segment;

  // --- Heart Rate Logic ---
  if (target_intensity_focus === 'hr') {
    // Check discriminating union 'type'
    if (target_intensity_value.type === 'hr_zone') {
      return `Zone ${target_intensity_value.zone}`;
    }
    if (target_intensity_value.type === 'hr_range') {
      return `${target_intensity_value.min_hr}-${target_intensity_value.max_hr} bpm`;
    }
  }

  // --- Pace Logic ---
  if (target_intensity_focus === 'pace') {
    // Check discriminating union 'type'
    if (target_intensity_value.type === 'pace_zone') {
      return `Zone ${target_intensity_value.zone}`;
    }
    if (target_intensity_value.type === 'pace_range') {
      // Assuming formatPace exists elsewhere or handles decimal minutes (e.g. 4.5 -> 4:30)
      return `${formatPace(target_intensity_value.min_pace)} - ${formatPace(target_intensity_value.max_pace)}/km`;
    }
  }

  // --- Power Logic ---
  if (target_intensity_focus === 'power') {
    // Check discriminating union 'type'
    if (target_intensity_value.type === 'power_zone') {
      return `Zone ${target_intensity_value.zone}`;
    }
    if (target_intensity_value.type === 'power_range') {
      return `${target_intensity_value.min_watts}-${target_intensity_value.max_watts} W`;
    }
  }

  // --- None / Fallback ---
  if (target_intensity_focus === 'none') {
    return '-';
  }

  return 'Custom target';
};

export const SegmentCard = ({ segment, onClick }: SegmentCardProps) => {
  const theme = useTheme();
  const compliancePercentage = segment.intensity_compliance_pct ?? 0;
  const complianceColor = getComplianceColor(compliancePercentage);
  const targetAchieved = segment.target_achieved ?? false;
  const activityColor = getActivityColor(segment.segment_name);
  const formattedName = formatActivityName(segment.segment_name);

  return (
    <Card
      elevation={0}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          boxShadow: theme.shadows[8],
          transform: 'translateY(-4px)',
          borderColor: alpha(activityColor, 0.4),
          '& .click-hint': {
            opacity: 1,
            transform: 'translateY(0)',
          }
        } : {},
      }}
      onClick={onClick}
    >
      {/* Activity Type Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 4,
          bgcolor: activityColor
        }}
      />

      <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: activityColor,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.7rem'
              }}
            >
              {formattedName}
            </Typography>
            {segment.set_rep && segment.set_total && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                REP {segment.set_rep}/{segment.set_total}
              </Typography>
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {segment.intensity_compliance_pct !== null && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: complianceColor,
                  fontSize: '0.75rem'
                }}
              >
                {compliancePercentage.toFixed(0)}%
              </Typography>
            )}
            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {segment.intensity_compliance_pct !== null && (
                <CircularProgress
                  variant="determinate"
                  value={Math.min(compliancePercentage, 100)}
                  size={36}
                  thickness={4.5}
                  sx={{
                    color: complianceColor,
                    position: 'absolute',
                    zIndex: 1,
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                      stroke: complianceColor,
                    }
                  }}
                />
              )}
              {segment.intensity_compliance_pct !== null && (
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={36}
                  thickness={4.5}
                  sx={{
                    color: 'transparent',
                    position: 'absolute',
                  }}
                />
              )}
              <Avatar
                sx={{
                  bgcolor: 'transparent',
                  color: targetAchieved ? '#4caf50' : '#ff9800',
                  width: 28,
                  height: 28,
                  zIndex: 2,
                }}
              >
                {targetAchieved ? <CheckCircle sx={{ fontSize: 20 }} /> : <Warning sx={{ fontSize: 20 }} />}
              </Avatar>
            </Box>
          </Stack>
        </Stack>

        {/* Metrics Grid */}
        <Grid container spacing={2} mb={2.5}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.65rem' }} display="block" gutterBottom>
              DURATION
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {formatDuration(segment.duration_seconds)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.65rem' }} display="block" gutterBottom>
              DISTANCE
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {formatDistance(segment.distance_meters)}
              <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>km</Typography>
            </Typography>
          </Grid>
        </Grid>

        {/* Target vs Actual */}
        <Box sx={{
          p: 1.5,
          bgcolor: alpha(theme.palette.action.hover, 0.05),
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.5),
          mb: 2.5
        }}>
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.6rem' }} display="block">
                TARGET
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {formatTargetIntensity(segment)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.6rem' }} display="block">
                ACTUAL
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {segment.avg_pace_per_km ? `${formatPace(segment.avg_pace_per_km)}/km` : '--'}
                {segment.avg_heartrate ? ` @ ${segment.avg_heartrate.toFixed(0)} bpm` : ''}
              </Typography>
            </Box>
          </Stack>
        </Box>


        {/* Click Hint (Visible on Hover) */}
        {onClick && (
          <Box
            className="click-hint"
            sx={{
              position: 'absolute',
              bottom: -2,
              left: 0,
              right: 0,
              opacity: 0,
              transform: 'translateY(5px)',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              pointerEvents: 'none',
              py: 1,
              px: 2,
            }}
          >
            <Typography
              variant="caption"
              color="primary"
              sx={{
                fontWeight: 700,
                fontSize: '0.7rem',
                textShadow: theme.palette.mode === 'dark'
                  ? '0 1px 4px rgba(0, 0, 0, 0.8)'
                  : '0 1px 4px rgba(255, 255, 255, 0.9)',
              }}
            >
              VIEW DETAILS
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
